import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Play } from "lucide-react";

export const Placeholder = ({ label, kind = "video", className = "", dark = false }) => (
  <div className={`relative w-full h-full overflow-hidden ${dark ? "bg-[#3a241d]" : "bg-[#EDE7DD]"} ${className}`} data-testid="media-placeholder" aria-label={`Placeholder: ${label}`}>
    <div className="absolute inset-0 placeholder-lines opacity-60" />
    <div className={`absolute inset-3 border border-dashed ${dark ? "border-[#FAF7F2]/25" : "border-[#2E1B16]/25"}`} />
    <div className="absolute inset-0 flex flex-col items-start justify-end p-5">
      <span className={`label-eyebrow text-[0.6rem] ${dark ? "text-[#38BDF8]" : "text-[#38BDF8]"}`}>Replace · {kind}</span>
      <span className={`mt-1.5 font-annotation text-2xl leading-none ${dark ? "text-[#FAF7F2]/80" : "text-[#2E1B16]/70"}`}>{label}</span>
    </div>
  </div>
);

// Renders a media item (uploaded/manifest video or image, or a labelled placeholder).
// Videos are lazy, muted, loop, and either autoplay in view or play on hover.
export const MediaFrame = ({ item, className = "", ratio = "aspect-[9/16]", autoplay = false, hoverPlay = true, onOpen, dark = false, sizes, priority = false }) => {
  const ref = useRef(null);
  const vid = useRef(null);
  const [inView, setInView] = useState(priority);
  const [hover, setHover] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!ref.current || priority) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { rootMargin: "200px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [priority]);

  useEffect(() => {
    const v = vid.current;
    if (!v) return;
    const shouldPlay = inView && !reduced && (autoplay || hover);
    if (shouldPlay) v.play().catch(() => {});
    else v.pause();
  }, [inView, hover, autoplay, reduced]);

  const openable = onOpen && item && !item.placeholder;
  const Wrapper = openable ? "button" : "div";

  return (
    <Wrapper
      ref={ref}
      type={openable ? "button" : undefined}
      onClick={openable ? () => onOpen(item) : undefined}
      onMouseEnter={() => hoverPlay && setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={openable ? `Open ${item.title || item.category || "video"}` : undefined}
      className={`group relative block w-full overflow-hidden ${ratio} ${openable ? "cursor-pointer" : ""} ${className}`}
      data-testid={item?.placeholder ? "media-frame-placeholder" : "media-frame"}
    >
      {!item || item.placeholder ? (
        <Placeholder label={item?.label || "Media"} kind={item?.kind} dark={dark} />
      ) : item.kind === "video" ? (
        <>
          <video
            ref={vid}
            src={inView ? item.src : undefined}
            poster={item.poster}
            muted
            loop
            playsInline
            preload={priority ? "auto" : "metadata"}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
          />
          {openable && (
            <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#FAF7F2]/90 text-[#2E1B16] grid place-items-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
              <Play size={16} fill="currentColor" />
            </span>
          )}
        </>
      ) : (
        <img
          src={item.src}
          alt={item.alt || item.title || ""}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          sizes={sizes}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
        />
      )}
    </Wrapper>
  );
};

export default MediaFrame;
